import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRightCircle, Eye, EyeOff, Phone, Mail, Globe, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { saveKoffieSession } from '../koffiereparatie-geleen/KoffieApp';
import { saveWhenconnectSession } from '../whenconnect/WhenconnectApp';
import { saveFiniziSession } from '../finizi/finiziSession';
import { webvonkClient, initKmfcClient } from '../lib/supabase';
import { useSession } from '../contexts/SessionContext';
import WebvonkLogoImage from '../assets/Webvonk.png';

// ─── Design tokens ────────────────────────────────────────────────────────────

export const wv = {
  bg:      '#f2f0eb',
  text:    '#0c0c0b',
  accent:  '#D4621A',   // dichter bij logo-oranje, voldoet WCAG AA op crème
  accentSmall: '#B85215', // donkerder variant voor kleine tekst (<18px)
  grey:    '#6b6865',  // donkerder voor WCAG AA
  body:    '#5b5953',
  lines:   '#dcd9d1',
  fontSans: "'Jost', system-ui, sans-serif",
  fontSerif: "'Playfair Display', Georgia, serif",
  px: 'clamp(24px, 4vw, 64px)',
  py: 'clamp(16px, 2.5vw, 32px)',
};

// ─── Global override for login screens ───────────────────────────────────────

const LoginGlobal = createGlobalStyle`
  body, html { font-family: ${wv.fontSans}; }
`;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
  40%            { opacity: 1;   transform: scale(1); }
`;

// ─── Shared shell ─────────────────────────────────────────────────────────────

const Shell = styled.div`
  min-height: 100vh;
  background: ${wv.bg};
  display: flex;
  flex-direction: column;
`;

// ─── Welcome screen ───────────────────────────────────────────────────────────

const WHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(20px, 3vh, 36px) ${wv.px};
`;

const WLogo = styled.img`
  height: 40px;
  object-fit: contain;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease 0.05s both;
  display: block;
`;

const WExternalLink = styled.a`
  font-family: ${wv.fontSans};
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${wv.body};
  text-decoration: none;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease 0.15s both;
  transition: color 0.2s;

  &:hover { color: ${wv.text}; }
`;

const WGrid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
`;

const WLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(48px, 8vh, 100px) ${wv.px};
  gap: clamp(24px, 3.5vh, 44px);
`;

const WRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: clamp(32px, 4vh, 56px) clamp(24px, 3vw, 48px);
  border-left: 1px solid ${wv.lines};
  overflow: hidden;
  position: relative;
`;

const WMain = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 ${wv.px} clamp(32px, 4vh, 56px);
  gap: clamp(16px, 2.5vh, 28px);
`;

// ─── Feature carousel ─────────────────────────────────────────────────────────

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const CarouselWrap = styled.div`
  width: 100%;
  max-width: 380px;
`;

const CarouselDots = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
`;

const CarouselDot = styled.button`
  width: ${p => p.$active ? '20px' : '6px'};
  height: 6px;
  border-radius: 3px;
  background: ${p => p.$active ? wv.accentSmall : wv.lines};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: width 0.3s ease, background 0.3s ease;
`;

const MockBarRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MockBarLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MockBarLabelText = styled.span`
  font-family: ${wv.fontSans};
  font-size: 10px;
  font-weight: 500;
  color: ${wv.body};
`;

const MockExampleBadge = styled.span`
  font-family: ${wv.fontSans};
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${wv.grey};
  border: 1px solid ${wv.lines};
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: auto;
`;

const CarouselSlide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${slideIn} 0.4s ease both;
`;

const CarouselTagline = styled.p`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(18px, 1.6vw, 22px);
  font-weight: 700;
  font-style: italic;
  color: ${wv.text};
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.01em;

  em {
    font-family: 'Playfair Display', Georgia, serif;
    font-weight: 700;
    font-style: italic;
    color: ${wv.accent};
  }
`;

const CarouselCard = styled.div`
  background: white;
  border: 1px solid ${wv.lines};
  border-radius: 14px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 4px 24px rgba(12,12,11,0.06);
`;

const CarouselLabel = styled.span`
  font-family: ${wv.fontSans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${wv.accentSmall};
`;

const CarouselTitle = styled.h3`
  font-family: ${wv.fontSans};
  font-size: 13px;
  font-weight: 600;
  color: ${wv.text};
  margin: 0;
  letter-spacing: 0.01em;
`;

const MockRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: ${wv.bg};
  border-radius: 8px;
  border: 1px solid ${wv.lines};
`;

const MockDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$color || wv.accent};
  flex-shrink: 0;
`;

const MockText = styled.div`
  height: 8px;
  border-radius: 4px;
  background: ${p => p.$dark ? '#d0cdc6' : '#e5e2da'};
  width: ${p => p.$w || '60%'};
`;

const MockBadge = styled.div`
  margin-left: auto;
  font-family: ${wv.fontSans};
  font-size: 10px;
  font-weight: 600;
  color: ${p => p.$color || wv.accent};
  background: ${p => p.$bg || 'rgba(199,90,10,0.1)'};
  padding: 2px 8px;
  border-radius: 20px;
  flex-shrink: 0;
`;

const MockBar = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${wv.lines};
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: ${p => p.$pct || '60%'};
    background: ${p => p.$color || wv.accent};
    border-radius: 3px;
  }
`;

const MockGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const MockStat = styled.div`
  background: ${wv.bg};
  border: 1px solid ${wv.lines};
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MockStatVal = styled.div`
  font-family: ${wv.fontSans};
  font-size: 18px;
  font-weight: 700;
  color: ${wv.text};
`;

const MockStatLabel = styled.div`
  font-family: ${wv.fontSans};
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${wv.grey};
`;

const MockInput = styled.div`
  height: 36px;
  border-radius: 8px;
  border: 1.5px solid ${wv.lines};
  background: white;
  display: flex;
  align-items: center;
  padding: 0 12px;
  gap: 8px;
`;

const MockInputLine = styled.div`
  height: 8px;
  border-radius: 4px;
  background: #e5e2da;
  width: ${p => p.$w || '50%'};
`;

const WLabel = styled.span`
  display: block;
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${wv.accentSmall};
  opacity: 0;
  animation: ${fadeUp} 0.5s ease 0.2s both;
`;

const WHeading = styled.h1`
  font-family: 'Playfair Display', Georgia, serif !important;
  font-size: clamp(28px, 4vw, 56px);
  font-weight: 900 !important;
  font-style: normal;
  font-synthesis: none;
  line-height: 0.92;
  letter-spacing: -0.02em;
  color: ${wv.text};
  margin: 0;
  opacity: 0;
  animation: ${fadeUp} 0.55s ease 0.3s both;
`;

const WHeadingAccent = styled.span`
  display: block;
  font-family: 'Playfair Display', Georgia, serif !important;
  font-weight: 900 !important;
  font-style: italic !important;
  font-synthesis: none;
  color: ${wv.accent};
`;

const WSubtitle = styled.p`
  font-family: ${wv.fontSans};
  font-size: clamp(14px, 1.1vw, 16px);
  font-weight: 400;
  color: ${wv.body};
  margin: 0;
  max-width: 30ch;
  opacity: 0;
  animation: ${fadeUp} 0.5s ease 0.4s both;
`;

const WActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  opacity: 0;
  animation: ${fadeUp} 0.5s ease 0.5s both;
`;

const WActionDivider = styled.div`
  width: 1px;
  height: 28px;
  background: ${wv.lines};
  flex-shrink: 0;
  align-self: center;
`;

const WActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const WActionHint = styled.span`
  font-family: ${wv.fontSans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${wv.body};
`;

export const BtnPrimary = styled.button`
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${wv.text};
  background: transparent;
  border: 1.5px solid ${wv.text};
  border-radius: 6px;
  padding: 13px 24px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${wv.text};
    color: ${wv.bg};
  }
`;

export const BtnSecondary = styled.button`
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${wv.text};
  background: none;
  border: none;
  border-bottom: 1px solid ${wv.text};
  padding: 0 0 5px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: ${wv.accent};
    border-color: ${wv.accent};
  }
`;

const WFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 0;
  margin: 0 ${wv.px} 16px;
  border-top: 1px solid ${wv.lines};
`;

const WFooterText = styled.span`
  font-family: ${wv.fontSans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${wv.body};
`;

// ─── Contact modal ────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 11, 0.75);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 999;
  animation: ${fadeIn} 0.2s ease both;
`;

const ModalBox = styled.div`
  background: ${wv.bg};
  border: 1px solid ${wv.lines};
  border-radius: 6px;
  padding: clamp(28px, 4vw, 44px);
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${fadeUp} 0.25s ease both;
`;

const ModalLabel = styled.span`
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${wv.accent};
`;

const ModalTitle = styled.h2`
  font-family: 'Playfair Display', Georgia, serif !important;
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 900 !important;
  font-synthesis: none;
  color: ${wv.text};
  margin: 0;
  line-height: 1.1;
`;

const ModalTitleAccent = styled.em`
  font-family: 'Playfair Display', Georgia, serif !important;
  font-style: italic !important;
  font-synthesis: none;
  color: ${wv.accent};
`;

const ModalBody = styled.p`
  font-family: ${wv.fontSans};
  font-size: 14px;
  color: ${wv.body};
  margin: 0;
  line-height: 1.6;
`;

const ModalContact = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ModalContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ${wv.fontSans};
  font-size: 14px;
  font-weight: 500;
  color: ${wv.text};
  text-decoration: none;
  transition: color 0.2s;

  &:hover { color: ${wv.accent}; }
`;

const ModalContactLabel = styled.span`
  font-family: ${wv.fontSans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${wv.grey};
  width: 36px;
  flex-shrink: 0;
`;

const ModalDivider = styled.div`
  height: 1px;
  background: ${wv.lines};
`;

const ModalLoginRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${wv.fontSans};
  font-size: 12px;
  color: ${wv.grey};
`;

const ModalLoginLink = styled.button`
  font-family: ${wv.fontSans};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${wv.text};
  background: none;
  border: none;
  border-bottom: 1px solid ${wv.text};
  padding: 0 0 1px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;

  &:hover { color: ${wv.accent}; border-color: ${wv.accent}; }
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: none;
  border: none;
  font-size: 16px;
  color: ${wv.grey};
  cursor: pointer;
  line-height: 1;
  padding: 4px;

  &:hover { color: ${wv.text}; }
`;

const ModalBoxWrap = styled.div`
  position: relative;
`;

// ─── Login screen ─────────────────────────────────────────────────────────────

const slideInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const LoginInner = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 420px;
  width: 100%;
  align-self: center;
  padding: clamp(32px, 6vh, 64px) ${wv.px};
`;

const LoginTitle = styled.h1`
  font-family: ${wv.fontSerif};
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 900;
  color: ${wv.text};
  margin: 0 0 36px;
  letter-spacing: -0.02em;
  line-height: 1;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
  animation: ${slideInUp} 0.35s ease both;
  animation-delay: ${p => p.$delay || '0s'};
`;

const FieldLabel = styled.label`
  font-family: ${wv.fontSans};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${wv.grey};
`;

const FieldBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: transparent;
  border: 1.5px solid ${p => p.$focused ? wv.text : wv.lines};
  border-radius: 8px;
  transition: border-color 0.2s;
`;

const FieldInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-family: ${wv.fontSans};
  font-size: 16px;
  font-weight: 400;
  color: ${wv.text};

  &::placeholder { color: transparent; }
  &:disabled { opacity: 0.5; }
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${p => p.$active ? wv.text : wv.lines};
  transition: color 0.2s;

  &:disabled { opacity: 0.25; cursor: default; }
  &:hover:not(:disabled) { color: ${wv.accent}; }
`;

const EyeBtn = styled(IconBtn)``;

const ErrorMessage = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  color: ${wv.text};
  background: ${wv.bg};
  border: 1.5px solid ${wv.text};
  cursor: pointer;

  &::before {
    content: '✕';
    font-size: 10px;
    opacity: 0.4;
    flex-shrink: 0;
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${wv.lines};
  border-top-color: ${wv.text};
  border-radius: 50%;
  animation: spin 0.7s linear infinite;

  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─── Mobile block screen ──────────────────────────────────────────────────────

const MobileBlock = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: ${wv.bg};
  }
`;

const MobileBlockHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: clamp(20px, 3vh, 36px) ${wv.px};
`;

const MobileBlockMain = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 ${wv.px} clamp(40px, 6vh, 64px);
  gap: clamp(20px, 3vh, 32px);
`;

const MobileBlockFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 0;
  margin: 0 ${wv.px} 16px;
  border-top: 1px solid ${wv.lines};
`;

// ─── Post-login screen ────────────────────────────────────────────────────────

const PostLoginScreen = styled.div`
  position: fixed;
  inset: 0;
  background: ${wv.bg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  z-index: 9999;
  animation: ${fadeIn} 0.4s ease forwards;
`;

const DotsRow = styled.div`
  display: flex;
  gap: 8px;
`;

const POST_LOGIN_COMPANIES = {
  'kmfc-shop': { name: 'KMFC Shop Online', logo: '/assets/KMFC_logo.png', bg: '#0c0c0b', scale: 1.5 },
  'koffiereparatie-geleen': { name: 'Koffiereparatie Geleen', logo: '/assets/Koffiereparatie-Geleen_logo.webp', bg: '#e6e5de' },
  'whenconnect': { name: 'Whenconnect', logo: '/assets/Whenconnect_logo.png', bg: '#ffffff' },
  'finizi': { name: 'Finizi', logo: '/assets/Finizi_logo.png', bg: '#ffffff' },
};

const slideFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const slideFromRight = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const sparkTravel = keyframes`
  0%   { left: 0%;   opacity: 0; }
  8%   { left: 6%;   opacity: 1; }
  50%  { left: 100%; opacity: 1; }
  58%  { left: 100%; opacity: 0; }
  100% { left: 100%; opacity: 0; }
`;

const glowSend = keyframes`
  0%   { opacity: 0; }
  8%   { opacity: 1; }
  30%  { opacity: 0; }
  100% { opacity: 0; }
`;

const glowReceive = keyframes`
  0%, 45% { opacity: 0; }
  55%     { opacity: 1; }
  85%     { opacity: 0; }
  100%    { opacity: 0; }
`;

const LogoPair = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
`;

const LogoTile = styled.div`
  position: relative;
  width: clamp(120px, 30vw, 200px);
  height: clamp(72px, 16vw, 110px);
  opacity: 0;
  animation: ${p => p.$fromRight ? slideFromRight : slideFromLeft} 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${p => p.$delay || '0s'} both;

  &::after {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 17px;
    box-shadow: 0 0 0 2px ${wv.accent}, 0 0 28px 4px rgba(212,98,26,0.35);
    opacity: 0;
    pointer-events: none;
    animation: ${p => p.$fromRight ? glowReceive : glowSend} 1.6s ease-in-out 0.7s infinite;
  }
`;

const LogoTileInner = styled.div`
  width: 100%;
  height: 100%;
  background: ${p => p.$bg || '#ffffff'};
  border: 1px solid ${wv.lines};
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(12,12,11,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(10px, 2.5vw, 18px);
  box-sizing: border-box;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const SparkTrack = styled.div`
  position: relative;
  width: clamp(48px, 8vw, 96px);
  height: 2px;
  margin: 0 6px;
  background: repeating-linear-gradient(90deg, ${wv.lines} 0 6px, transparent 6px 11px);
  opacity: 0;
  animation: ${fadeIn} 0.4s ease 0.5s both;
`;

const Spark = styled.span`
  position: absolute;
  top: 50%;
  left: 0;
  width: 10px;
  height: 10px;
  margin: -5px 0 0 -5px;
  border-radius: 50%;
  background: ${wv.accent};
  box-shadow: 0 0 10px 3px rgba(212,98,26,0.55);
  opacity: 0;
  animation: ${sparkTravel} 1.6s cubic-bezier(0.45, 0, 0.25, 1) 0.7s infinite;

  &::before {
    content: '';
    position: absolute;
    top: 4px;
    right: 6px;
    width: 26px;
    height: 2px;
    border-radius: 1px;
    background: linear-gradient(90deg, transparent, ${wv.accent});
  }
`;

const PostLoginCaption = styled.p`
  font-family: ${wv.fontSans};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${wv.grey};
  margin: 0;
  opacity: 0;
  animation: ${fadeUp} 0.5s ease 0.7s both;

  strong {
    font-weight: 600;
    color: ${wv.text};
  }
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${wv.text};
  animation: ${dotPulse} 1.4s ease-in-out infinite;
  animation-delay: ${p => p.$delay || '0s'};
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getIsLightMode() {
  const saved = localStorage.getItem('kmfc-theme');
  if (saved === 'light') return true;
  if (saved === 'dark') return false;
  return window.matchMedia('(prefers-color-scheme: light)').matches;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function LoginScreen() {
  const navigate = useNavigate();
  const { login } = useSession();
  const [showWelcome, setShowWelcome] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showPostLogin, setShowPostLogin] = useState(false);
  const [isLight, setIsLight] = useState(getIsLightMode);
  const [usernameFocused, setUsernameFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [slideOrder] = useState(() => {
    const arr = [0, 1, 2, 3, 4, 5, 6];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  });
  const [activeSlide, setActiveSlide] = useState(0);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const passwordRef = useRef(null);
  const touchStartX = useRef(null);
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const slides = [
    {
      tagline: <CarouselTagline>Alles in <em>een</em> overzicht.</CarouselTagline>,
      label: 'Bestellingen', title: 'Inkomende orders',
      content: (
        <>
          <MockRow><MockDot $color={wv.accent} /><MockText $w="55%" $dark /><MockBadge $color={wv.accentSmall} $bg="rgba(212,98,26,0.1)">Nieuw</MockBadge></MockRow>
          <MockRow><MockDot $color={wv.text} /><MockText $w="65%" $dark /><MockBadge $color={wv.text} $bg="rgba(12,12,11,0.07)">Betaald</MockBadge></MockRow>
          <MockRow><MockDot $color={wv.grey} /><MockText $w="70%" /><MockBadge $color={wv.grey} $bg="rgba(138,135,128,0.1)">Klaar</MockBadge></MockRow>
        </>
      ),
    },
    {
      tagline: <CarouselTagline>Krijg meer <em>inzicht</em> dan ooit.</CarouselTagline>,
      label: 'Statistieken', title: 'Omzet & prestaties',
      content: (
        <>
          <MockGrid>
            <MockStat><MockStatVal>€ 1.284</MockStatVal><MockStatLabel>Deze week</MockStatLabel></MockStat>
            <MockStat><MockStatVal>47</MockStatVal><MockStatLabel>Bestellingen</MockStatLabel></MockStat>
          </MockGrid>
          <MockBarRow>
            <MockBarLabel>
              <MockBarLabelText>Weekdoel</MockBarLabelText>
              <MockBarLabelText>72%</MockBarLabelText>
            </MockBarLabel>
            <MockBar $pct="72%" $color={wv.accent} />
          </MockBarRow>
          <MockBarRow>
            <MockBarLabel>
              <MockBarLabelText>Herhaalaankopen</MockBarLabelText>
              <MockBarLabelText>45%</MockBarLabelText>
            </MockBarLabel>
            <MockBar $pct="45%" $color={wv.text} />
          </MockBarRow>
        </>
      ),
    },
    {
      tagline: <CarouselTagline>Jouw menu, altijd <em>up-to-date</em>.</CarouselTagline>,
      label: 'Menu', title: 'Producten & beschikbaarheid',
      content: (
        <>
          <MockRow><MockDot $color={wv.accent} /><MockText $w="50%" $dark /><MockBadge $color={wv.accentSmall} $bg={`rgba(212,98,26,0.1)`}>Actief</MockBadge></MockRow>
          <MockRow><MockDot $color={wv.accent} /><MockText $w="65%" $dark /><MockBadge $color={wv.accentSmall} $bg={`rgba(212,98,26,0.1)`}>Actief</MockBadge></MockRow>
          <MockRow><MockDot $color={wv.grey} /><MockText $w="45%" $dark /><MockBadge $color={wv.body} $bg="rgba(138,135,128,0.1)">Uit</MockBadge></MockRow>
        </>
      ),
    },
    {
      tagline: <CarouselTagline>Geen <em>aanvraag</em> meer gemist.</CarouselTagline>,
      label: 'Contactformulier', title: 'Nieuwe inzendingen',
      content: (
        <>
          <MockRow><MockDot $color={wv.accent} /><MockText $w="45%" $dark /><MockBadge $color={wv.accentSmall} $bg="rgba(212,98,26,0.1)">Nieuw</MockBadge></MockRow>
          <MockRow><MockDot $color={wv.accent} /><MockText $w="60%" $dark /><MockBadge $color={wv.accentSmall} $bg="rgba(212,98,26,0.1)">Nieuw</MockBadge></MockRow>
          <MockRow><MockDot $color={wv.text} /><MockText $w="50%" $dark /><MockBadge $color={wv.text} $bg="rgba(12,12,11,0.07)">Beantwoord</MockBadge></MockRow>
        </>
      ),
    },
    {
      tagline: <CarouselTagline>Pas je website zelf <em>aan</em>.</CarouselTagline>,
      label: 'Website', title: 'Inhoud bewerken',
      content: (
        <>
          <MockBarLabelText>Titel</MockBarLabelText>
          <MockInput><MockInputLine $w="45%" /></MockInput>
          <MockBarLabelText>Tekst</MockBarLabelText>
          <MockRow style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
            <MockText $w="90%" /><MockText $w="75%" /><MockText $w="55%" />
          </MockRow>
          <MockBadge $color={wv.accentSmall} $bg="rgba(212,98,26,0.1)">Opgeslagen</MockBadge>
        </>
      ),
    },
    {
      tagline: <CarouselTagline>Altijd de juiste <em>openingstijden</em>.</CarouselTagline>,
      label: 'Openingstijden', title: 'Deze week',
      content: (
        <>
          <MockRow><MockBarLabelText>Maandag – vrijdag</MockBarLabelText><MockBadge $color={wv.text} $bg="rgba(12,12,11,0.07)">09:00 – 18:00</MockBadge></MockRow>
          <MockRow><MockBarLabelText>Zaterdag</MockBarLabelText><MockBadge $color={wv.text} $bg="rgba(12,12,11,0.07)">10:00 – 17:00</MockBadge></MockRow>
          <MockRow><MockBarLabelText>Zondag</MockBarLabelText><MockBadge $color={wv.body} $bg="rgba(138,135,128,0.1)">Gesloten</MockBadge></MockRow>
        </>
      ),
    },
    {
      tagline: <CarouselTagline>Laat je klanten voor je <em>spreken</em>.</CarouselTagline>,
      label: 'Ervaringen', title: 'Reviews van klanten',
      content: (
        <>
          <MockRow style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <MockText $w="35%" $dark /><MockBadge $color={wv.accentSmall} $bg="rgba(212,98,26,0.1)">★★★★★</MockBadge>
            </div>
            <MockText $w="85%" /><MockText $w="55%" />
          </MockRow>
          <MockRow style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <MockText $w="30%" $dark /><MockBadge $color={wv.accentSmall} $bg="rgba(212,98,26,0.1)">★★★★☆</MockBadge>
            </div>
            <MockText $w="70%" />
          </MockRow>
        </>
      ),
    },
  ];

  useEffect(() => {
    if (reducedMotion || carouselPaused) return;
    const t = setInterval(() => setActiveSlide(i => (i + 1) % slides.length), 3500);
    return () => clearInterval(t);
  }, [carouselPaused, reducedMotion]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e) => setIsLight(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const getErrorMessage = (error) => {
    const msg = error?.message?.toLowerCase() || '';
    if (msg.includes('geen toegang') || msg.includes('ander bedrijf')) return 'U heeft geen toegang tot dit bedrijf.';
    if (msg.includes('invalid login credentials') || msg.includes('invalid') || msg.includes('incorrect') || msg.includes('gebruikersnaam of wachtwoord')) return 'Foute inloggegevens.';
    if (msg.includes('too many requests')) return 'Teveel verzoeken, wacht even en probeer opnieuw.';
    return 'Er is iets misgegaan, neem contact met ons op.';
  };

  const handleLoginClick = () => {
    setShowWelcome(false);
  };

  const advanceFromUsername = () => {
    if (!username.trim()) return;
    setShowPasswordField(true);
    setTimeout(() => passwordRef.current?.focus(), 50);
  };

  const isFormValid = username.trim() !== '' && password !== '';

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setErrorMessage('');

    try {
      const normalizedUsername = username.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
      const email = `${normalizedUsername}@webvonk.app`;

      const { data: authData, error: authError } = await webvonkClient.auth.signInWithPassword({
        email,
        password,
      });

      if (authError || !authData?.user) {
        throw new Error('invalid login credentials');
      }

      const userId = authData.user.id;

      const { data: profile, error: profileError } = await webvonkClient
        .from('Users')
        .select('User_id, Username, Company_slug, Role_id, Companies(Company_name)')
        .eq('User_id', userId)
        .single();

      if (profileError || !profile) {
        throw new Error('invalid login credentials');
      }

      // Super admin
      if (profile.Company_slug === null && profile.Role_id === 1) {
        const superAdminData = {
          id: userId,
          username: profile.Username,
          role_id: 1,
          access_token: authData.session.access_token,
          refresh_token: authData.session.refresh_token,
          expires_at: authData.session.expires_at,
          session_expires_at: Date.now() + 30 * 24 * 60 * 60 * 1000,
          token_expires_at: authData.session.expires_at ? authData.session.expires_at * 1000 : Date.now() + 3600_000,
        };
        localStorage.setItem('webvonk_superadmin', JSON.stringify(superAdminData));
        setShowPostLogin(true);
        setTimeout(() => { window.location.href = '/#/company-select'; }, 2500);
        return;
      }

      const ALLOWED = ['kmfc-shop', 'koffiereparatie-geleen', 'whenconnect', 'finizi'];
      if (!ALLOWED.includes(profile.Company_slug)) {
        throw new Error('geen toegang');
      }

      // TIJDELIJK UITGESCHAKELD — mobiele blokkering op login
      // if (profile.Company_slug === 'kmfc-shop' && window.innerWidth < 768) {
      //   setErrorMessage('Download de app om het dashboard op een telefoon te bekijken.');
      //   setLoading(false);
      //   return;
      // }

      const roleMap = {
        1: { slug: 'admin', can_view_new_orders: true, can_view_order_history: true, can_manage_settings: true, can_edit_product_data: true, can_change_product_status: true, can_view_product_statistics: true, can_view_revenue_statistics: true },
        2: { slug: 'manager', can_view_new_orders: true, can_view_order_history: true, can_manage_settings: true, can_edit_product_data: true, can_change_product_status: true, can_view_product_statistics: true, can_view_revenue_statistics: true },
        3: { slug: 'medewerker', can_view_new_orders: true, can_view_order_history: false, can_manage_settings: false, can_edit_product_data: false, can_change_product_status: true, can_view_product_statistics: false, can_view_revenue_statistics: false },
      };

      const userData = {
        id: userId,
        username: profile.Username,
        company_slug: profile.Company_slug,
        company_name: profile.Companies?.Company_name ?? '',
        role_id: profile.Role_id,
        role: roleMap[profile.Role_id] ?? roleMap[3],
        access_token: authData.session.access_token,
        refresh_token: authData.session.refresh_token,
        expires_at: authData.session.expires_at,
        login_time: new Date().toISOString(),
      };

      if (profile.Company_slug === 'koffiereparatie-geleen') {
        saveKoffieSession(userData);
        setShowPostLogin(profile.Company_slug);
        setTimeout(() => { window.location.href = '/#/Koffiereparatie/dashboard'; }, 2500);
        return;
      }

      if (profile.Company_slug === 'whenconnect') {
        saveWhenconnectSession(userData);
        setShowPostLogin(profile.Company_slug);
        setTimeout(() => { navigate('/Whenconnect/dashboard', { replace: true }); }, 2500);
        return;
      }

      if (profile.Company_slug === 'finizi') {
        saveFiniziSession(userData);
        setShowPostLogin(profile.Company_slug);
        setTimeout(() => { navigate('/Finizi/binnenkort', { replace: true }); }, 2500);
        return;
      }

      await initKmfcClient();
      const savedUser = { ...userData, expires_at: authData.session.expires_at };
      localStorage.setItem('kmfc_user', JSON.stringify(savedUser));

      setShowPostLogin(profile.Company_slug);
      setTimeout(() => {
        login(savedUser);
        navigate('/KMFC/orders', { replace: true });
      }, 2500);

    } catch (err) {
      setErrorMessage(getErrorMessage(err));
      setShakeKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  };

  if (showPostLogin) {
    const company = POST_LOGIN_COMPANIES[showPostLogin];
    return (
      <PostLoginScreen>
        {company ? (
          <>
            <LogoPair>
              <LogoTile>
                <LogoTileInner>
                  <img src={WebvonkLogoImage} alt="Webvonk" />
                </LogoTileInner>
              </LogoTile>
              <SparkTrack><Spark /></SparkTrack>
              <LogoTile $fromRight $delay="0.15s">
                <LogoTileInner $bg={company.bg}>
                  <img src={`${process.env.PUBLIC_URL}${company.logo}`} alt={company.name} style={company.scale ? { transform: `scale(${company.scale})` } : undefined} />
                </LogoTileInner>
              </LogoTile>
            </LogoPair>
            <PostLoginCaption><strong>{company.name}</strong></PostLoginCaption>
          </>
        ) : (
          <img src={WebvonkLogoImage} alt="Webvonk" style={{ height: 40, objectFit: 'contain' }} />
        )}
        <DotsRow>
          <Dot $delay="0s" />
          <Dot $delay="0.2s" />
          <Dot $delay="0.4s" />
        </DotsRow>
      </PostLoginScreen>
    );
  }

  if (showWelcome) {
    return (
      <>
      <MobileBlock>
        <LoginGlobal />
        <MobileBlockHeader>
          <WLogo src={WebvonkLogoImage} alt="Webvonk" />
        </MobileBlockHeader>
        <MobileBlockMain>
          <WLabel>Klantportaal</WLabel>
          <WHeading>
            Mijn
            <WHeadingAccent>Webvonk<span style={{ color: wv.text }}>.</span></WHeadingAccent>
          </WHeading>
          <WSubtitle style={{ maxWidth: '28ch' }}>
            Mijn Webvonk is ontworpen voor een groter scherm.
            Open het portaal op je laptop of computer.
          </WSubtitle>
          <div style={{ opacity: 0, animation: `${fadeUp.getName ? '' : 'fadeUp'} 0.5s ease 0.5s both` }}>
            <a
              href="https://webvonk.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: wv.fontSans, fontSize: 11, fontWeight: 500,
                letterSpacing: '0.18em', textTransform: 'uppercase',
                color: wv.body, textDecoration: 'none',
                borderBottom: `1px solid ${wv.lines}`, paddingBottom: 4,
              }}
            >
              webvonk.com ↗
            </a>
          </div>
        </MobileBlockMain>
        <MobileBlockFooter>
          <WFooterText>© {new Date().getFullYear()} Webvonk</WFooterText>
          <WFooterText>Laat jouw merk online vonken.</WFooterText>
        </MobileBlockFooter>
      </MobileBlock>

      <Shell style={{ display: 'none' }} className="desktop-only">
        <style>{`@media (min-width: 769px) { .desktop-only { display: flex !important; } }`}</style>
        <LoginGlobal />
        <WHeader>
          <WLogo src={WebvonkLogoImage} alt="Webvonk" />
          <WExternalLink href="https://webvonk.com" target="_blank" rel="noopener noreferrer">
            webvonk.com&nbsp;↗
          </WExternalLink>
        </WHeader>

        <WGrid>
          <WLeft>
            <WLabel>Klantportaal</WLabel>
            <WHeading>
              Welkom bij
              <WHeadingAccent>Mijn Webvonk<span style={{ color: wv.text }}>.</span></WHeadingAccent>
            </WHeading>
            <WSubtitle style={{ marginBottom: 'clamp(16px, 3vh, 40px)' }}>Beheer jouw bedrijf op één plek.</WSubtitle>
            <WActions>
              <WActionGroup>
                <WActionHint>Al klant?</WActionHint>
                <BtnPrimary onClick={handleLoginClick}>Inloggen ⟶</BtnPrimary>
              </WActionGroup>
              <WActionDivider />
              <WActionGroup>
                <WActionHint>Nog geen klant?</WActionHint>
                <BtnSecondary onClick={() => setShowContactModal(true)}>Aan de slag →</BtnSecondary>
              </WActionGroup>
            </WActions>
          </WLeft>

          <WRight
            onMouseEnter={() => setCarouselPaused(true)}
            onMouseLeave={() => setCarouselPaused(false)}
            onFocus={() => setCarouselPaused(true)}
            onBlur={() => setCarouselPaused(false)}
            onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
              if (touchStartX.current === null) return;
              const diff = touchStartX.current - e.changedTouches[0].clientX;
              if (Math.abs(diff) > 40) setActiveSlide(i => diff > 0 ? (i + 1) % slides.length : (i - 1 + slides.length) % slides.length);
              touchStartX.current = null;
            }}
          >
            <CarouselWrap>
              <CarouselSlide key={activeSlide}>
                {slides[slideOrder[activeSlide]].tagline}
                <CarouselCard>
                  <CarouselLabel>{slides[slideOrder[activeSlide]].label}</CarouselLabel>
                  <CarouselTitle>{slides[slideOrder[activeSlide]].title}</CarouselTitle>
                  {slides[slideOrder[activeSlide]].content}
                </CarouselCard>
              </CarouselSlide>
            </CarouselWrap>
          </WRight>
        </WGrid>

        <WFooter>
          <WFooterText>© {new Date().getFullYear()} Webvonk</WFooterText>
          <WFooterText>Laat jouw merk online vonken.</WFooterText>
        </WFooter>

        {showContactModal && (
          <ModalOverlay onClick={() => setShowContactModal(false)}>
            <ModalBoxWrap onClick={e => e.stopPropagation()}>
              <ModalBox>
                <ModalCloseBtn onClick={() => setShowContactModal(false)}>✕</ModalCloseBtn>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <ModalLabel>Vrijblijvend gesprek</ModalLabel>
                  <ModalTitle>Neem <ModalTitleAccent>contact</ModalTitleAccent> op</ModalTitle>
                </div>
                <ModalBody>
                  Wil je weten wat ik voor jouw bedrijf kan betekenen? Stuur een bericht of mail mij, ik reageer snel.
                </ModalBody>
                <ModalContact>
                  <ModalContactItem href="tel:+31612531446">
                    <Phone size={15} strokeWidth={1.5} style={{ flexShrink: 0 }} />+31 6 12531446
                  </ModalContactItem>
                  <ModalContactItem href="mailto:info@webvonk.com">
                    <Mail size={15} strokeWidth={1.5} style={{ flexShrink: 0 }} />info@webvonk.com
                  </ModalContactItem>
                  <ModalContactItem href="https://webvonk.com" target="_blank" rel="noopener noreferrer">
                    <Globe size={15} strokeWidth={1.5} style={{ flexShrink: 0 }} />webvonk.com
                  </ModalContactItem>
                </ModalContact>
                <ModalDivider />
                <ModalLoginRow>
                  Ben je al klant?&nbsp;
                  <ModalLoginLink onClick={() => { setShowContactModal(false); handleLoginClick(); }}>
                    Log hier in →
                  </ModalLoginLink>
                </ModalLoginRow>
              </ModalBox>
            </ModalBoxWrap>
          </ModalOverlay>
        )}
      </Shell>
      </>
    );
  }

  return (
    <Shell>
      <LoginGlobal />
      <div style={{ padding: `${wv.py} ${wv.px} 0` }}>
        <button
          onClick={() => setShowWelcome(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: wv.fontSans, fontSize: 11, fontWeight: 500,
            letterSpacing: '0.16em', textTransform: 'uppercase',
            color: wv.grey, padding: 0, transition: 'color 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = wv.text}
          onMouseLeave={e => e.currentTarget.style.color = wv.grey}
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Terug
        </button>
      </div>
      <LoginInner>
        <LoginTitle>Inloggen</LoginTitle>

        <form onSubmit={handleLogin}>
          <FieldGroup $delay="0s">
            <FieldLabel htmlFor="username">Gebruikersnaam</FieldLabel>
            <FieldBox $focused={usernameFocused}>
              <FieldInput
                id="username"
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (!e.target.value.trim() && showPasswordField) setShowPasswordField(false);
                }}
                onFocus={() => setUsernameFocused(true)}
                onBlur={() => setUsernameFocused(false)}
                onKeyDown={(e) => e.key === 'Enter' && advanceFromUsername()}
                disabled={loading}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
              />
              {username.trim() && !showPasswordField && (
                <IconBtn $active type="button" onClick={advanceFromUsername}>
                  <ArrowRightCircle size={22} strokeWidth={1.5} />
                </IconBtn>
              )}
            </FieldBox>
          </FieldGroup>

          {showPasswordField && (
            <FieldGroup key={shakeKey} $delay="0s" style={shakeKey > 0 ? { animation: 'shake 0.45s ease both' } : {}}>
              <FieldLabel htmlFor="password">Wachtwoord</FieldLabel>
              <FieldBox $focused={passwordFocused}>
                <FieldInput
                  ref={passwordRef}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <EyeBtn $active type="button" onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                  {showPassword ? <EyeOff size={18} strokeWidth={1.5} /> : <Eye size={18} strokeWidth={1.5} />}
                </EyeBtn>
                {loading ? (
                  <Spinner />
                ) : (
                  <IconBtn $active={isFormValid} type="submit" disabled={!isFormValid}>
                    <ArrowRightCircle size={22} strokeWidth={1.5} />
                  </IconBtn>
                )}
              </FieldBox>
            </FieldGroup>
          )}
        </form>

        {errorMessage && (
          <ErrorMessage onClick={() => setErrorMessage('')}>{errorMessage}</ErrorMessage>
        )}
      </LoginInner>
    </Shell>
  );
}
